import { Vector3, AbstractMesh, Scene, AmmoJSPlugin, PhysicsImpostor, Quaternion } from '@babylonjs/core';
import { IPhysicsEngine, IPhysicsBody, PhysicsShapeType, PhysicsBodyConfig } from './IPhysicsEngine';
import { logger } from '../../core/Logger';

/**
 * AmmoJS implementation of IPhysicsBody
 */
class AmmoPhysicsBody implements IPhysicsBody {
  constructor(
    public mesh: AbstractMesh,
    public impostor: PhysicsImpostor
  ) {}

  getLinearVelocity(): Vector3 {
    return this.impostor.getLinearVelocity() || Vector3.Zero();
  }

  setLinearVelocity(velocity: Vector3): void {
    this.impostor.setLinearVelocity(velocity);
  }

  getAngularVelocity(): Vector3 {
    return this.impostor.getAngularVelocity() || Vector3.Zero();
  }

  setAngularVelocity(velocity: Vector3): void {
    this.impostor.setAngularVelocity(velocity);
  }

  applyForce(force: Vector3, contactPoint?: Vector3): void {
    if (contactPoint) {
      this.impostor.applyForce(force, contactPoint);
    } else {
      this.impostor.applyForce(force, this.mesh.getAbsolutePosition());
    }
  }

  applyImpulse(impulse: Vector3, contactPoint?: Vector3): void {
    if (contactPoint) {
      this.impostor.applyImpulse(impulse, contactPoint);
    } else {
      this.impostor.applyImpulse(impulse, this.mesh.getAbsolutePosition());
    }
  }

  applyTorque(_torque: Vector3): void {
    // AmmoJS doesn't have direct torque support in the impostor API
    // We can simulate it using forces at offset points
    logger.physics.warn('applyTorque not fully supported in AmmoJS, use applyAngularImpulse instead');
  }

  applyAngularImpulse(impulse: Vector3): void {
    this.impostor.setAngularVelocity(
      this.getAngularVelocity().add(impulse)
    );
  }

  setPosition(position: Vector3): void {
    // For kinematic bodies, we need to directly set the physics body transform
    // NOT just the mesh position, because the physics engine will override mesh.position
    if (this.impostor.physicsBody) {
      const ammoBody = this.impostor.physicsBody;
      // Get the Ammo transform
      const transform = ammoBody.getWorldTransform();
      // Create new Ammo vector for the position
      const ammoVec = new Ammo.btVector3(position.x, position.y, position.z);
      transform.setOrigin(ammoVec);
      ammoBody.setWorldTransform(transform);
      // For kinematic bodies, also update motion state
      const motionState = ammoBody.getMotionState();
      if (motionState) {
        motionState.setWorldTransform(transform);
      }
      // Clean up
      Ammo.destroy(ammoVec);
    }
    // Also update mesh position to stay in sync
    this.mesh.position.copyFrom(position);
  }

  setRotation(rotation: Vector3): void {
    // Keep mesh in sync (but do NOT rely on it to drive Ammo)
    this.mesh.rotation.copyFrom(rotation);

    // For kinematic bodies, we must update the physics body transform rotation,
    // otherwise the physics engine will overwrite mesh.rotation from the old body rotation.
    if (this.impostor.physicsBody) {
      const ammoBody = this.impostor.physicsBody;
      const transform = ammoBody.getWorldTransform();

      const q = Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z);
      const ammoQuat = new Ammo.btQuaternion(q.x, q.y, q.z, q.w);
      transform.setRotation(ammoQuat);

      ammoBody.setWorldTransform(transform);
      const motionState = ammoBody.getMotionState();
      if (motionState) {
        motionState.setWorldTransform(transform);
      }

      Ammo.destroy(ammoQuat);
    }
  }

  setMass(mass: number): void {
    this.impostor.setMass(mass);
  }

  setFriction(friction: number): void {
    this.impostor.friction = friction;
  }

  setRestitution(restitution: number): void {
    this.impostor.restitution = restitution;
  }

  setEnabled(enabled: boolean): void {
    if (enabled) {
      this.impostor.wakeUp();
    } else {
      this.impostor.sleep();
    }
  }

  isEnabled(): boolean {
    // AmmoJS doesn't expose sleep state easily
    return true;
  }

  setBodyGravity(gravity: Vector3): void {
    if (this.impostor.physicsBody) {
      const ammoBody = this.impostor.physicsBody;
      const ammoGravity = new Ammo.btVector3(gravity.x, gravity.y, gravity.z);
      ammoBody.setGravity(ammoGravity);
      Ammo.destroy(ammoGravity);
    }
  }

  dispose(): void {
    this.impostor.dispose();
  }
}

/**
 * AmmoJS implementation of IPhysicsEngine
 */
export class AmmoPhysicsEngine implements IPhysicsEngine {
  private plugin: AmmoJSPlugin | null = null;
  private bodies: Map<AbstractMesh, IPhysicsBody> = new Map();
  private ready: boolean = false;

  constructor(private scene: Scene) {}

  async initialize(gravity: Vector3): Promise<void> {
    // Load AmmoJS
    await Ammo();
    
    // Create plugin with useDeltaForWorldStep = false
    // This allows us to control physics timestep manually for time scaling (slow-mo, etc.)
    this.plugin = new AmmoJSPlugin(false);
    
    // Enable physics on scene
    this.scene.enablePhysics(gravity, this.plugin);
    
    this.ready = true;
    logger.physics.log('✅ AmmoJS Physics Engine initialized (manual timestep mode)');
  }

  /**
   * Set the physics simulation time step.
   * Call this each frame with the scaled delta time for time scaling effects.
   * @param timeStep Time step in seconds
   */
  setTimeStep(timeStep: number): void {
    if (this.plugin) {
      this.plugin.setTimeStep(timeStep);
    }
  }

  createBody(
    mesh: AbstractMesh,
    shapeType: PhysicsShapeType,
    config: PhysicsBodyConfig
  ): IPhysicsBody {
    if (!this.ready) {
      throw new Error('Physics engine not initialized. Call initialize() first.');
    }

    logger.physics.log(`[AmmoPhysicsEngine] Creating body for mesh: ${mesh.name}`);
    logger.physics.log(`   Shape: ${shapeType}, Mass: ${config.mass}`);

    // Map our shape type to AmmoJS impostor type
    const impostorType = this.mapShapeTypeToImpostor(shapeType);
    logger.physics.log(`   Mapped to impostor type: ${impostorType}`);
    
    // CRITICAL: Check if scene has physics enabled
    if (!this.scene.isPhysicsEnabled()) {
      logger.physics.error(`   ❌ SCENE PHYSICS NOT ENABLED! Cannot create impostor.`);
      throw new Error('Scene physics not enabled');
    }
    
    logger.physics.log(`   ✅ Scene physics enabled: ${this.scene.isPhysicsEnabled()}`);
    logger.physics.log(`   Physics plugin: ${!!this.plugin}`);
    
    // Create impostor
    logger.physics.log(`   Creating PhysicsImpostor...`);
    // DEBUG: Log mesh state at physics creation time
    mesh.computeWorldMatrix(true);
    mesh.refreshBoundingInfo({ applySkeleton: true });
    const boundingInfo = mesh.getBoundingInfo();
    const localCenter = boundingInfo.boundingBox.centerWorld.subtract(mesh.getAbsolutePosition());
    logger.physics.log(`   🔍 [PHYSICS DEBUG] Mesh state at impostor creation:`);
    logger.physics.log(`      position: ${mesh.position.toString()}`);
    logger.physics.log(`      scaling: ${mesh.scaling.toString()}`);
    logger.physics.log(`      absolutePosition: ${mesh.getAbsolutePosition().toString()}`);
    logger.physics.log(`      absoluteScaling: ${mesh.absoluteScaling.toString()}`);
    logger.physics.log(`      parent: ${mesh.parent?.name ?? 'none'}`);
    logger.physics.log(`      🎯 boundingBox.centerWorld: ${boundingInfo.boundingBox.centerWorld.toString()}`);
    logger.physics.log(`      🎯 boundingBox.center (local): ${boundingInfo.boundingBox.center.toString()}`);
    logger.physics.log(`      🎯 CENTER OFFSET FROM MESH POSITION: ${localCenter.toString()} (length: ${localCenter.length().toFixed(4)})`);
    try {
      const impostor = new PhysicsImpostor(
        mesh,
        impostorType,
        {
          mass: config.mass,
          friction: config.friction ?? 0.5,
          restitution: config.restitution ?? 0.0
        },
        this.scene
      );
      
      logger.physics.log(`   PhysicsImpostor created: ${!!impostor}`);
      logger.physics.log(`   Impostor.physicsBody exists: ${!!impostor.physicsBody}`);
      logger.physics.log(`   mesh.physicsImpostor now exists: ${!!mesh.physicsImpostor}`);
      
      if (!impostor.physicsBody) {
        logger.physics.error(`   ❌ PhysicsImpostor created but has no physicsBody!`);
      }
      
      // Apply collision filtering if specified
      if (config.collisionGroup !== undefined || config.collisionMask !== undefined) {
        logger.physics.log(`   Applying collision filtering: group=${config.collisionGroup}, mask=${config.collisionMask}`);
        this.setCollisionFiltering(impostor, config.collisionGroup, config.collisionMask);
      }

      // Apply additional config
      if (config.linearDamping !== undefined) {
        impostor.setLinearVelocity(impostor.getLinearVelocity()?.scale(1 - config.linearDamping) || Vector3.Zero());
      }
      
      if (config.angularDamping !== undefined) {
        impostor.setAngularVelocity(impostor.getAngularVelocity()?.scale(1 - config.angularDamping) || Vector3.Zero());
      }

      // Create wrapped body
      const body = new AmmoPhysicsBody(mesh, impostor);
      this.bodies.set(mesh, body);
      
      // WORKAROUND: PhysicsImpostor constructor should set mesh.physicsImpostor,
      // but sometimes it doesn't. Manually ensure it's set.
      if (!mesh.physicsImpostor) {
        logger.physics.warn(`   ⚠️ mesh.physicsImpostor was not set by PhysicsImpostor constructor, setting manually`);
        (mesh as any).physicsImpostor = impostor;
      }
      
      logger.physics.log(`   ✅ AmmoPhysicsBody wrapper created`);
      logger.physics.log(`   Final check - mesh.physicsImpostor: ${!!mesh.physicsImpostor}`);
      
      return body;
    } catch (error) {
      logger.physics.error(`   ❌ Exception creating PhysicsImpostor:`, error);
      throw error;
    }
  }

  removeBody(body: IPhysicsBody): void {
    body.dispose();
    this.bodies.delete(body.mesh);
  }

  setCollisionFilter(body: IPhysicsBody, collisionGroup?: number, collisionMask?: number): void {
    const ammoBody = body as AmmoPhysicsBody;
    this.setCollisionFiltering(ammoBody.impostor, collisionGroup, collisionMask);
  }

  setGravity(gravity: Vector3): void {
    if (!this.plugin) {
      throw new Error('Physics engine not initialized');
    }

    // Keep scene gravity in sync
    this.scene.gravity = gravity;

    // Ensure the active physics engine picks up the new gravity too.
    // (Some BabylonJS backends won't react to changing scene.gravity alone.)
    this.scene.getPhysicsEngine()?.setGravity(gravity);
  }

  getGravity(): Vector3 {
    return this.scene.gravity;
  }

  update(_deltaTime: number): void {
    // AmmoJS physics is automatically updated by BabylonJS scene
    // No manual update needed
  }

  dispose(): void {
    // Dispose all bodies
    for (const body of this.bodies.values()) {
      body.dispose();
    }
    this.bodies.clear();
    
    // Dispose physics
    if (this.scene.getPhysicsEngine()) {
      this.scene.getPhysicsEngine()?.dispose();
    }
    
    this.ready = false;
    this.plugin = null;
  }

  isReady(): boolean {
    return this.ready;
  }

  getPlugin(): any {
    return this.plugin;
  }

  /**
   * Map our abstracted shape types to AmmoJS impostor types
   */
  private mapShapeTypeToImpostor(shapeType: PhysicsShapeType): number {
    switch (shapeType) {
      case PhysicsShapeType.BOX:
        return PhysicsImpostor.BoxImpostor;
      case PhysicsShapeType.SPHERE:
        return PhysicsImpostor.SphereImpostor;
      case PhysicsShapeType.CAPSULE:
        return PhysicsImpostor.CapsuleImpostor;
      case PhysicsShapeType.CYLINDER:
        return PhysicsImpostor.CylinderImpostor;
      case PhysicsShapeType.MESH:
        return PhysicsImpostor.MeshImpostor;
      case PhysicsShapeType.CONVEX_HULL:
        return PhysicsImpostor.ConvexHullImpostor;
      default:
        return PhysicsImpostor.BoxImpostor;
    }
  }

  /**
   * Create a static triangle mesh collider directly from mesh vertex data.
   * This is used for static collision meshes from GLB files where the standard
   * PhysicsImpostor approach fails (MeshImpostor/ConvexHullImpostor don't create bodies).
   * 
   * ⚠️ IMPORTANT LIMITATIONS:
   * - Only works for STATIC (non-moving) geometry!
   * - Vertices are baked into world space at creation time
   * - If the mesh moves/rotates/animates, collision geometry will NOT follow
   * - For moving collision geometry, use kinematic bodies with simple shapes instead
   * 
   * @param mesh The mesh to create a static collider for (must be non-moving!)
   * @param config Physics configuration (friction, restitution - mass is ignored, always 0)
   * @returns IPhysicsBody wrapping the Ammo body
   */
  createStaticTriangleMesh(
    mesh: AbstractMesh,
    config: PhysicsBodyConfig
  ): IPhysicsBody | null {
    if (!this.ready || !this.plugin) {
      throw new Error('Physics engine not initialized. Call initialize() first.');
    }

    logger.physics.log(`[AmmoPhysicsEngine] Creating STATIC triangle mesh for: ${mesh.name}`);

    // Warn if mesh has animations (triangle mesh won't follow animations!)
    if (mesh.animations && mesh.animations.length > 0) {
      logger.physics.warn(`   ⚠️ WARNING: Mesh ${mesh.name} has animations but using STATIC triangle mesh!`);
      logger.physics.warn(`   Collision geometry will NOT follow animations. Consider using a simple kinematic shape instead.`);
    }

    // Get vertex data from mesh
    const positions = mesh.getVerticesData('position');
    const indices = mesh.getIndices();

    if (!positions || !indices) {
      logger.physics.error(`   ❌ Mesh ${mesh.name} has no vertex/index data - cannot create triangle mesh!`);
      logger.physics.log(`   positions: ${positions ? positions.length / 3 : 'null'} vertices, indices: ${indices ? indices.length : 'null'}`);
      return null;
    }

    logger.physics.log(`   Vertices: ${positions.length / 3}, Triangles: ${indices.length / 3}`);

    try {
      // ============================================================================
      // APPROACH: Bake mesh transform into world-space vertices
      // ============================================================================
      // We transform all vertices to world space and use an identity transform
      // for the rigid body. This avoids coordinate space mismatches but means
      // the collision geometry is FROZEN at creation time and cannot move.
      //
      // Alternative approach (for moving geometry):
      // - Keep vertices in LOCAL space
      // - Update the rigid body's transform every frame to match mesh.worldMatrix
      // - Use kinematic body (mass=0, CF_KINEMATIC_OBJECT flag)
      // - Call ammoBody.setWorldTransform() in update loop
      // ============================================================================
      
      const triangleMesh = new Ammo.btTriangleMesh(true, true);

      // Get the world matrix to transform vertices to world space
      // This "bakes" the current position/rotation/scale into the collision geometry
      const worldMatrix = mesh.computeWorldMatrix(true);
      
      // Check if mesh has negative scale (from parent hierarchy)
      // Negative scale flips triangle winding, causing inside-out collisions
      const absScaling = mesh.absoluteScaling ?? mesh.scaling;
      const scaleSign = absScaling.x * absScaling.y * absScaling.z;
      const hasNegativeScale = scaleSign < 0;
      
      if (hasNegativeScale) {
        logger.physics.log(`   ⚠️ Mesh ${mesh.name} has negative scale (${absScaling.toString()}), reversing triangle winding`);
      }

      // Add triangles from mesh (transforming to world space)
      for (let i = 0; i < indices.length; i += 3) {
        // If negative scale, swap winding order to fix normals (swap i1 and i2)
        const i0 = indices[i];
        const i1 = hasNegativeScale ? indices[i + 2] : indices[i + 1];
        const i2 = hasNegativeScale ? indices[i + 1] : indices[i + 2];

        // Get vertex positions in LOCAL space
        const v0Local = new Vector3(positions[i0 * 3], positions[i0 * 3 + 1], positions[i0 * 3 + 2]);
        const v1Local = new Vector3(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
        const v2Local = new Vector3(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);

        // Transform to WORLD space
        const v0 = Vector3.TransformCoordinates(v0Local, worldMatrix);
        const v1 = Vector3.TransformCoordinates(v1Local, worldMatrix);
        const v2 = Vector3.TransformCoordinates(v2Local, worldMatrix);

        // Pass world space vertices to Ammo
        const av0 = new Ammo.btVector3(v0.x, v0.y, v0.z);
        const av1 = new Ammo.btVector3(v1.x, v1.y, v1.z);
        const av2 = new Ammo.btVector3(v2.x, v2.y, v2.z);

        triangleMesh.addTriangle(av0, av1, av2, true);

        Ammo.destroy(av0);
        Ammo.destroy(av1);
        Ammo.destroy(av2);
      }

      // Create collision shape from triangle mesh
      const shape = new Ammo.btBvhTriangleMeshShape(triangleMesh, true, true);

      // Create motion state at ORIGIN since vertices are already in world space
      const transform = new Ammo.btTransform();
      transform.setIdentity(); // Identity transform = no translation/rotation
      
      const motionState = new Ammo.btDefaultMotionState(transform);

      // Static body has mass 0 and no inertia
      const localInertia = new Ammo.btVector3(0, 0, 0);
      const rbInfo = new Ammo.btRigidBodyConstructionInfo(0, motionState, shape, localInertia);
      
      // Set friction and restitution
      rbInfo.set_m_friction(config.friction ?? 1.0);
      rbInfo.set_m_restitution(config.restitution ?? 0.0);

      const ammoBody = new Ammo.btRigidBody(rbInfo);

      // Set collision flags for static object
      ammoBody.setCollisionFlags(ammoBody.getCollisionFlags() | 1); // CF_STATIC_OBJECT = 1

      // Get collision group and mask
      const collisionGroup = config.collisionGroup ?? 1; // Default to STATIC
      const collisionMask = config.collisionMask ?? 0xFF; // Default to collide with all

      // Add to physics world WITH collision filtering
      // This is crucial - addRigidBody has an overload that takes group/mask
      const physicsWorld = this.plugin.world;
      physicsWorld.addRigidBody(ammoBody, collisionGroup, collisionMask);

      logger.physics.log(`   ✅ Static triangle mesh created successfully for ${mesh.name}`);
      logger.physics.log(`   Triangles: ${indices.length / 3}, collisionGroup: ${collisionGroup}, collisionMask: ${collisionMask}`);

      // Create a minimal impostor wrapper for compatibility
      // We create a NoImpostor just to have something, but store our ammo body
      const impostor = new PhysicsImpostor(
        mesh,
        PhysicsImpostor.NoImpostor,
        { mass: 0 },
        this.scene
      );

      // Monkey-patch the physicsBody to our manual ammo body
      (impostor as any).physicsBody = ammoBody;
      (impostor as any)._ammoTriangleMesh = triangleMesh; // Keep reference to avoid GC
      (impostor as any)._ammoShape = shape;
      (impostor as any)._ammoMotionState = motionState; // Keep reference
      
      // WORKAROUND: NoImpostor doesn't set mesh.physicsImpostor, so we need to do it manually
      if (!mesh.physicsImpostor) {
        (mesh as any).physicsImpostor = impostor;
      }
      
      // Do NOT destroy transform, localInertia, or rbInfo here - they are owned by Ammo objects
      // Destroying them will cause memory corruption!

      // Create wrapped body
      const body = new AmmoPhysicsBody(mesh, impostor);
      this.bodies.set(mesh, body);

      return body;
    } catch (error) {
      logger.physics.error(`   ❌ Exception creating triangle mesh:`, error);
      return null;
    }
  }

  /**
   * Set collision filtering on an impostor via low-level Ammo.js broadphase proxy access
   */
  private setCollisionFiltering(
    impostor: PhysicsImpostor,
    collisionGroup?: number,
    collisionMask?: number
  ): void {
    const ammoBody = impostor.physicsBody;
    if (!ammoBody) {
      logger.physics.warn('Cannot set collision filtering: physics body not available');
      return;
    }

    try {
      // The safest way to apply collision filtering in Ammo.js is to remove the body
      // from the world and re-add it with the correct group/mask
      const world = this.plugin?.world;
      if (!world) {
        logger.physics.warn('Cannot set collision filtering: physics world not available');
        return;
      }

      // Remove from world
      world.removeRigidBody(ammoBody);
      
      // Re-add with collision filtering
      const group = collisionGroup ?? 1;
      const mask = collisionMask ?? 0xFF;
      world.addRigidBody(ammoBody, group, mask);

      logger.physics.debug(
        `Set collision filtering: group=${group.toString(16)}, mask=${mask.toString(16)}`
      );
    } catch (error) {
      logger.physics.error('Failed to set collision filtering:', error);
    }
  }

  /**
   * Perform a physics raycast using Ammo.js world.rayTest()
   */
  raycast(
    from: Vector3,
    to: Vector3,
    collisionMask?: number
  ): { hitPoint: Vector3; hitNormal: Vector3; distance: number } | null {
    const world = this.plugin?.world;
    if (!world || typeof (world as any).rayTest !== 'function') {
      return null;
    }

    const Ammo = (globalThis as any).Ammo;
    if (!Ammo) return null;

    // Create Ammo vectors for raycast
    const fromAmmo = new Ammo.btVector3(from.x, from.y, from.z);
    const toAmmo = new Ammo.btVector3(to.x, to.y, to.z);

    // Create raycast callback
    const rayCallback = new Ammo.ClosestRayResultCallback(fromAmmo, toAmmo);

    // Set collision filter mask if provided
    if (collisionMask !== undefined) {
      rayCallback.set_m_collisionFilterMask(collisionMask);
    }

    try {
      // Perform raycast
      (world as any).rayTest(fromAmmo, toAmmo, rayCallback);

      // Check if we hit something
      if (rayCallback.hasHit()) {
        // Get hit point
        const hitPointAmmo = rayCallback.get_m_hitPointWorld();
        const hitPoint = new Vector3(
          hitPointAmmo.x(),
          hitPointAmmo.y(),
          hitPointAmmo.z()
        );

        // Get hit normal
        const hitNormalAmmo = rayCallback.get_m_hitNormalWorld();
        const hitNormal = new Vector3(
          hitNormalAmmo.x(),
          hitNormalAmmo.y(),
          hitNormalAmmo.z()
        );

        // Calculate distance
        const distance = Vector3.Distance(from, hitPoint);

        // Clean up Ammo objects
        Ammo.destroy(fromAmmo);
        Ammo.destroy(toAmmo);
        Ammo.destroy(rayCallback);

        return { hitPoint, hitNormal, distance };
      }

      // No hit - clean up and return null
      Ammo.destroy(fromAmmo);
      Ammo.destroy(toAmmo);
      Ammo.destroy(rayCallback);
      return null;
    } catch (error) {
      logger.physics.error('Raycast failed:', error);
      // Clean up on error
      try {
        Ammo.destroy(fromAmmo);
        Ammo.destroy(toAmmo);
        Ammo.destroy(rayCallback);
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
      return null;
    }
  }

  /**
   * Debug: Get collision object info from Ammo physics world
   */
  getCollisionObjectsDebugInfo(): Array<{
    position: Vector3;
    shape: string;
    isStatic: boolean;
  }> {
    const world = this.plugin?.world;
    if (!world) return [];

    const Ammo = (globalThis as any).Ammo;
    if (!Ammo) return [];

    const debugInfo: Array<{
      position: Vector3;
      shape: string;
      isStatic: boolean;
    }> = [];

    try {
      // Get all collision objects from the world
      const collisionObjectArray = world.getCollisionObjectArray();
      const numObjects = collisionObjectArray.size();

      for (let i = 0; i < numObjects; i++) {
        const collisionObject = collisionObjectArray.at(i);
        const rigidBody = Ammo.castObject(collisionObject, Ammo.btRigidBody);
        
        // Get transform
        const transform = rigidBody.getWorldTransform();
        const origin = transform.getOrigin();
        
        // Get shape type
        const shape = collisionObject.getCollisionShape();
        const shapeType = shape.getShapeType();
        
        // Determine shape name
        let shapeName = 'unknown';
        if (shapeType === 21) shapeName = 'triangle_mesh';
        else if (shapeType === 8) shapeName = 'box';
        else if (shapeType === 10) shapeName = 'capsule';
        else if (shapeType === 0) shapeName = 'sphere';
        
        // Check if static (mass = 0)
        const mass = 1.0 / rigidBody.getInvMass();
        const isStatic = mass === 0 || !isFinite(mass);
        
        debugInfo.push({
          position: new Vector3(origin.x(), origin.y(), origin.z()),
          shape: shapeName,
          isStatic
        });
      }
    } catch (error) {
      logger.physics.error('Failed to get collision objects debug info:', error);
    }

    return debugInfo;
  }
}
